<template>
    <rkm-form-group
        v-if="range"
        class="mb-0"
        label-font-weight="normal"
    >
        <label v-if="label">
            {{ label }}
        </label>

        <rkm-row>
            <rkm-col>
                <ValidationProvider
                    :mode="validationMode"
                    :name="localStartLabel"
                    :rules="startRules"
                    :vid="uniqueId"
                    slim
                    v-slot="{ validated, valid, errors }"
                >
                    <rkm-form-group
                        :invalid-feedback="errors[0]"
                        :label="localStartLabel + (startRules && startRules.required ? ' *' : '')"
                        :state="startRules ? (validated ? valid : null) : null"
                        v-bind="rangeOptionalProps"
                    >
                        <b-input-group>
                            <b-form-input
                                :disabled="disabled"
                                :readonly="readonly"
                                :state="rules ? (validated ? valid ? null : false : null) : null"
                                :value="formattedStartValue"
                                @blur="handleStartBlur"
                                placeholder="DD/MM/AAAA"
                                ref="DateStartInput"
                                type="text"
                            />

                            <b-input-group-append>
                                <b-form-datepicker
                                    :disabled="disabled"
                                    :max="localStartMaxDate"
                                    :min="localStartMinDate"
                                    :readonly="readonly"
                                    :right="right"
                                    :state="startRules ? (validated ? valid : null) : null"
                                    button-only
                                    calendar-width="240px"
                                    class="rkm-datepicker"
                                    hide-header
                                    locale="pt-BR"
                                    v-bind="labelProperties"
                                    v-model="value.start"
                                    value-as-date
                                >
                                    <template #button-content>
                                        <rkm-icon :icon="['far', 'calendar-alt']" />
                                    </template>
                                </b-form-datepicker>
                            </b-input-group-append>
                        </b-input-group>
                    </rkm-form-group>
                </ValidationProvider>
            </rkm-col>

            <rkm-col>
                <ValidationProvider
                    :mode="validationMode"
                    :name="localEndLabel"
                    :rules="endRules"
                    :vid="uniqueId"
                    slim
                    v-slot="{ validated, valid, errors }"
                >
                    <rkm-form-group
                        :invalid-feedback="errors[0]"
                        :label="localEndLabel + (endRules && endRules.required ? ' *' : '')"
                        :state="endRules ? (validated ? valid : null) : null"
                        v-bind="rangeOptionalProps"
                    >
                        <b-input-group>
                            <b-form-input
                                :disabled="disabled"
                                :readonly="readonly"
                                :state="rules ? (validated ? valid ? null : false : null) : null"
                                :value="formattedEndValue"
                                @blur="handleEndBlur"
                                placeholder="DD/MM/AAAA"
                                ref="DateEndInput"
                                type="text"
                            />

                            <b-input-group-append>
                                <b-form-datepicker
                                    :disabled="disabled"
                                    :max="localEndMaxDate"
                                    :min="localEndMinDate"
                                    :readonly="readonly"
                                    :right="right"
                                    :state="endRules ? (validated ? valid : null) : null"
                                    button-only
                                    calendar-width="240px"
                                    class="rkm-datepicker"
                                    hide-header
                                    locale="pt-BR"
                                    v-bind="labelProperties"
                                    v-model="value.end"
                                    value-as-date
                                >
                                    <template #button-content>
                                        <rkm-icon :icon="['far', 'calendar-alt']" />
                                    </template>
                                </b-form-datepicker>
                            </b-input-group-append>
                        </b-input-group>
                    </rkm-form-group>
                </ValidationProvider>
            </rkm-col>
        </rkm-row>
    </rkm-form-group>

    <ValidationProvider
        v-else
        :mode="validationMode"
        :name="label"
        :rules="rules"
        :vid="uniqueId"
        slim
        v-slot="{ validated, valid, errors }"
    >
        <rkm-form-group
            :invalid-feedback="errors[0]"
            :label="label + (rules && rules.required ? ' *' : '')"
            :state="rules ? (validated ? valid : null) : null"
        >
            <b-input-group>
                <b-form-input
                    :disabled="disabled"
                    :readonly="readonly"
                    :state="rules ? (validated ? valid ? null : false : null) : null"
                    :value="formattedValue"
                    @blur="handleBlur"
                    placeholder="DD/MM/AAAA"
                    ref="DateInput"
                    type="text"
                />

                <b-input-group-append>
                    <b-form-datepicker
                        :disabled="disabled"
                        :max="localMaxDate"
                        :min="localMinDate"
                        :readonly="readonly"
                        :right="right"
                        :state="rules ? (validated ? valid : null) : null"
                        :value="value"
                        @input="$emit('input', $event)"
                        button-only
                        calendar-width="240px"
                        class="rkm-datepicker"
                        hide-header
                        locale="pt-BR"
                        v-bind="labelProperties"
                        value-as-date
                    >
                        <template #button-content>
                            <rkm-icon :icon="['far', 'calendar-alt']" />
                        </template>
                    </b-form-datepicker>
                </b-input-group-append>
            </b-input-group>
        </rkm-form-group>
    </ValidationProvider>
</template>

<script>
// Components.
import {
    BFormDatepicker,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
} from 'bootstrap-vue'
import {ValidationProvider} from 'vee-validate'

// Mixins.
import {string} from './../mixins'

// Others.
import cleave from 'cleave.js'
import moment from 'moment'

export default {
    components: {
        BFormDatepicker,
        BFormInput,
        BInputGroup,
        BInputGroupAppend,
        ValidationProvider,
    },
    computed: {
        rules() {
            return this.disabled ? null : this.localValidations
        },
        startRules() {
            return this.disabled ? null : this.localStartValidations
        },
        endRules() {
            return this.disabled ? null : this.localEndValidations
        },
        localMinDate() {
            const date = moment(this.minDate, this.dateFormat)

            return date.isValid() ? date.toDate() : null
        },
        localMaxDate() {
            const date = moment(this.maxDate, this.dateFormat)

            return date.isValid() ? date.toDate() : null
        },
        localStartMinDate() {
            if (this.value.end && !this.noDefaultRangeLimit) {
                const endLessOneYear = moment(this.value.end).subtract(1, 'year').toDate()

                if (!this.localMinDate || this.localMinDate < endLessOneYear) {
                    return endLessOneYear
                }
            }

            return this.localMinDate
        },
        localStartMaxDate() {
            return this.value.end ? this.value.end : this.localMaxDate
        },
        localEndMinDate() {
            return this.value.start ? this.value.start : this.localMinDate
        },
        localEndMaxDate() {
            if (this.value.start && !this.noDefaultRangeLimit) {
                const startPlusOneYear = moment(this.value.start).add(1, 'year').toDate()

                if (!this.localMaxDate || this.localMaxDate > startPlusOneYear) {
                    return startPlusOneYear
                }
            }

            return this.localMaxDate
        },
        localValidations() {
            const validations = {}

            if (this.localMinDate) {
                validations.minDate = this.localMinDate
            }

            if (this.localMaxDate) {
                validations.maxDate = this.localMaxDate
            }

            return Object.assign({}, this.validations, validations)
        },
        localStartValidations() {
            const validations = {}

            if (this.localStartMinDate) {
                validations.minDate = this.localStartMinDate
            }

            if (this.value.end) {
                validations.maxDate = this.value.end
            }
            else if (this.localStartMaxDate) {
                validations.maxDate = this.localStartMaxDate
            }

            return Object.assign({}, this.startValidations, validations)
        },
        localEndValidations() {
            const validations = {}

            if (this.localEndMaxDate) {
                validations.maxDate = this.localEndMaxDate
            }

            if (this.value.start) {
                validations.minDate = this.value.start
            }
            else if (this.localStartMinDate) {
                validations.minDate = this.localStartMinDate
            }

            return Object.assign({}, this.endValidations, validations)
        },
        formattedValue() {
            const date = moment(this.value)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        formattedStartValue() {
            const date = moment(this.value.start)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        formattedEndValue() {
            const date = moment(this.value.end)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        localStartLabel() {
            return this.startLabel || 'Início'
        },
        localEndLabel() {
            return this.endLabel || 'Fim'
        },
        rangeOptionalProps() {
            const props = {}

            if (this.rangeInline) {
                props['label-cols'] = 'auto'
            }

            return props
        },
    },
    data: () => ({
        labelProperties: {
            'label-calendar': 'Calendário',
            'label-close-button': 'Fechar',
            'label-current-month': 'Mês atual',
            'label-help': 'Use as teclas de cursor para navegar pela data do calendário',
            'label-nav': 'Navegação no calendário',
            'label-next-decade': 'Próxima década',
            'label-next-month': 'Próximo mês',
            'label-next-year': 'Próximo ano',
            'label-no-date-selected': 'Nenhuma data selecionada',
            'label-prev-decade': 'Década anterior',
            'label-prev-month': 'Mês anterior',
            'label-prev-year': 'Ano anterior',
            'label-reset-button': 'Resetar',
            'label-today': 'Hoje',
            'label-today-button': 'Selecionar hoje',
        },
    }),
    mixins: [string],
    methods: {
        handleBlur(event) {
            const date = moment(event.target.value, this.dateFormat)

            if (date.isValid()) {
                this.$emit('input', date.toDate())
            } else {
                event.target.value = null
                this.$emit('input', event.target.value)
            }

            this.$emit('blur', event)
        },
        handleStartBlur(event) {
            const date = moment(event.target.value, this.dateFormat)

            if (date.isValid()) {
                this.value.start = date.toDate()
            } else {
                this.value.start = event.target.value = null
            }

            this.$emit('blur', event)
        },
        handleEndBlur(event) {

            const date = moment(event.target.value, this.dateFormat, true)

            if (date.isValid()) {

                this.value.end = date.toDate()
            }
            else {

                this.value.end = event.target.value = null
            }

            this.$emit('blur', event)
        },
    },
    mounted() {
        const cleaveOptions = {
            date: true,
            datePattern: ['d', 'm', 'Y'],
            delimiter: '/',
        }

        if (this.range) {
            new cleave(this.$refs.DateStartInput.$el, cleaveOptions)
            new cleave(this.$refs.DateEndInput.$el, cleaveOptions)
        } else {
            new cleave(this.$refs.DateInput.$el, cleaveOptions)
        }
    },
    props: {
        dateFormat: {type: String, default: 'DD/MM/YYYY'},
        disabled: {type: Boolean},
        endLabel: {type: String},
        endValidations: {type: Object},
        label: {type: String},
        maxDate: {type: String},
        minDate: {type: String},
        noDefaultRangeLimit: {type: Boolean, default: false},
        range: {type: Boolean},
        rangeInline: {type: Boolean, default: true},
        readonly: {type: Boolean},
        right: {type: Boolean},
        startLabel: {type:String},
        startValidations: {type: Object},
        validationMode: {type: String, default: 'lazy'},
        validations: {type: Object},
        value: {type: [Date, Object, String], default: null},
    },
}
</script>

<style>
.rkm-datepicker > .dropdown-menu.dropdown-menu-right {
    left: auto !important;
    right: auto !important;
}
</style>